import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import EcfBadge from './EcfBadge';
import { descargarPdf, generateEcfRiPdf } from '../../../../lib/ecfRI';
import { ecfService, type RiSlot } from '../../../../services/ecf.service';

/**
 * Panel del Paso 5: las representaciones impresas.
 *
 * El portal pide un PDF por casilla y son once, no diez: el consumo (32) se parte entre el de
 * 250 mil o más —página completa— y el de menos, que va en formato ticket.
 *
 * Cada PDF tiene que corresponder a un comprobante de la simulación del paso 4 que la DGII haya
 * ACEPTADO: el QR lleva el código de seguridad de esa firma concreta, y la DGII lo escanea para
 * comprobar que resuelve contra un comprobante suyo. Por eso el e-NCF de cada casilla lo propone
 * el backend entre los aceptados, en vez de dejarlo a mano.
 */

interface RepresentacionImpresaPanelProps {
  /** Se pasa para re-consultar cuando cambia la configuración del emisor. */
  emisorRnc: string | null;
}

/** El portal limita la suma de los archivos subidos. */
const LIMITE_MB = 10;

export default function RepresentacionImpresaPanel({
  emisorRnc,
}: RepresentacionImpresaPanelProps) {
  const [slots, setSlots] = useState<RiSlot[]>([]);
  const [elegido, setElegido] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [bajando, setBajando] = useState<string | null>(null);
  const [todos, setTodos] = useState(false);
  const [pesos, setPesos] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const s = await ecfService.riPlan();
      setSlots(s);
      setElegido(
        Object.fromEntries(s.filter((x) => x.encf).map((x) => [x.clave, x.encf as string]))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el plan de impresas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, emisorRnc]);

  const descargar = async (slot: RiSlot) => {
    const encf = elegido[slot.clave];
    if (!encf) return;
    setBajando(slot.clave);
    try {
      const data = await ecfService.riData(encf);
      const { blob, filename } = await generateEcfRiPdf(data);
      descargarPdf(blob, filename);
      setPesos((p) => ({ ...p, [slot.clave]: blob.size }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo generar el PDF');
    } finally {
      setBajando(null);
    }
  };

  const descargarTodos = async () => {
    const listos = slots.filter((s) => elegido[s.clave]);
    if (!listos.length) return;
    setTodos(true);
    let fallos = 0;
    const nuevos: Record<string, number> = {};
    for (const slot of listos) {
      try {
        const data = await ecfService.riData(elegido[slot.clave]);
        const { blob, filename } = await generateEcfRiPdf(data);
        nuevos[slot.clave] = blob.size;
        descargarPdf(blob, filename);
        // Los navegadores descartan descargas disparadas en ráfaga; un respiro entre archivos
        // evita que se pierdan las últimas sin avisar.
        await new Promise((r) => setTimeout(r, 350));
      } catch {
        fallos++;
      }
    }
    setPesos((p) => ({ ...p, ...nuevos }));
    setTodos(false);
    if (fallos) toast.warning(`${listos.length - fallos} generados · ${fallos} con error`);
    else toast.success(`${listos.length} representaciones impresas descargadas`);
  };

  const conEncf = slots.filter((s) => elegido[s.clave]).length;
  const totalBytes = Object.values(pesos).reduce((a, b) => a + b, 0);
  const totalMb = totalBytes / (1024 * 1024);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-4 rounded-lg border bg-red-50 text-red-700 border-red-200 text-sm">
          <i className="ri-error-warning-line mr-2"></i>
          {error}
        </div>
      )}

      {conEncf < slots.length && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm">
          <p className="font-medium">
            <i className="ri-error-warning-line mr-1"></i>
            Faltan {slots.length - conEncf} casillas por cubrir
          </p>
          <p className="text-xs mt-1">
            Solo se pueden imprimir comprobantes de la simulación que la DGII haya aceptado. Si
            falta alguna casilla, vuelve al Paso 4 y comprueba que ese tipo quedó aceptado.
          </p>
        </div>
      )}

      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium text-gray-900">
          1. Genera los PDF de la simulación
        </p>
        <p className="text-xs text-gray-500">
          Cada archivo lleva el QR con el código de seguridad del comprobante que la DGII aceptó en
          el Paso 4, más su código y fecha de firma. Los tipo 32 salen en formato ticket y el resto
          en página completa, como los modelos de la DGII.
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={descargarTodos}
            disabled={todos || !conEncf}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <i
              className={`${todos ? 'ri-loader-4-line animate-spin' : 'ri-download-2-line'} mr-2`}
            ></i>
            {todos ? 'Generando...' : `Descargar las ${conEncf}`}
          </button>
          {totalBytes > 0 && (
            <span
              className={`text-xs ${totalMb > LIMITE_MB ? 'text-red-600 font-medium' : 'text-gray-500'}`}
            >
              Suma de lo generado: {totalMb.toFixed(2)} MB
              {totalMb > LIMITE_MB && ` · el portal admite ${LIMITE_MB} MB como máximo`}
            </span>
          )}
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <p className="text-sm font-medium text-gray-900">
            Casillas del portal
            <span className="ml-2 text-xs font-normal text-gray-500">
              ({conEncf} de {slots.length} listas)
            </span>
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Casilla
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Comprobante
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Formato
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  PDF
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {slots.map((s) => (
                <tr key={s.clave} className="hover:bg-gray-50">
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{s.label}</td>
                  <td className="px-4 py-2">
                    {s.opciones.length > 1 ? (
                      <select
                        value={elegido[s.clave] ?? ''}
                        onChange={(e) =>
                          setElegido((p) => ({ ...p, [s.clave]: e.target.value }))
                        }
                        className="px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {s.opciones.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    ) : s.encf ? (
                      <span className="text-xs font-mono text-gray-900">{s.encf}</span>
                    ) : (
                      <EcfBadge variant="danger">Sin comprobante aceptado</EcfBadge>
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-600">
                    {s.docType === '32' ? 'Ticket' : 'Página completa'}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <button
                      onClick={() => descargar(s)}
                      disabled={!elegido[s.clave] || bajando === s.clave || todos}
                      className="text-blue-600 hover:text-blue-900 disabled:text-gray-300 disabled:cursor-not-allowed"
                      title="Descargar PDF"
                    >
                      <i
                        className={
                          bajando === s.clave ? 'ri-loader-4-line animate-spin' : 'ri-file-pdf-line'
                        }
                      ></i>
                    </button>
                    {pesos[s.clave] && (
                      <span className="ml-2 text-[11px] text-gray-400">
                        {(pesos[s.clave] / 1024).toFixed(0)} KB
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg p-4">
        <p className="text-sm font-medium text-gray-900 mb-1">2. Súbelos al portal</p>
        <p className="text-xs text-gray-500">
          En el Paso 5 del portal hay una casilla por tipo. Sube en cada una el PDF que le
          corresponde: la DGII revisa el formato contra la Ley 32-23 y escanea el QR, así que tiene
          que abrir sin problemas.
        </p>
      </div>
    </div>
  );
}
